import {registerSendDefaultsRemote, SendDefaults, syncSendDefaults} from '@/helpers/sendDefaults.helper';
import {UserPreferences} from '@/interfaces/clientOnly/userPreferences.interface';

/**
 * Connects "Meine Versand-Standards" (sendDefaults.helper, pure) with the user-login-handler `me/preferences`:
 * registers the remote save and syncs the local cache with the server answer (after login and when the settings
 * dialog opens). Imported by notificationPreferences.helper, which loads `me/preferences` after the login.
 */
const userApi = async () => (await import('@/classes/api/user-login-handler.api')).UserLoginHandlerApi;

registerSendDefaultsRemote({
    save: async (payload) => (await userApi()).saveMySendDefaults(payload),
});

/** Applies an already loaded `me/preferences` answer (null: endpoint not available) */
export function applyServerSendDefaults(userId: unknown, server: UserPreferences | null): Promise<SendDefaults> {
    return syncSendDefaults(userId, server);
}

/**
 * Where the defaults shown in "Meine Versand-Standards" come from: the user account ('server'), only this browser because
 * the backend has no `me/preferences` yet ('browser'), or the local cache because the server did not answer ('offline')
 */
export type SendDefaultsSource = 'server' | 'browser' | 'offline';

/** Loads `me/preferences` and syncs: current defaults of the user and whether the server answered */
export async function refreshSendDefaultsWithSource(userId: unknown): Promise<{ defaults: SendDefaults; source: SendDefaultsSource }> {
    let server: UserPreferences | null = null;
    try {
        server = await (await userApi()).getMyPreferences();
    } catch (e) {
        server = null;
    }
    const defaults = await syncSendDefaults(userId, server);
    if (server) {
        return {defaults, source: 'server'};
    }
    const {backendFeatures} = await import('@/helpers/featureProbe.helper');
    return {defaults, source: backendFeatures.preferences === false ? 'browser' : 'offline'};
}

/** Loads `me/preferences` and syncs (opening "Meine Versand-Standards"): current defaults of the user */
export async function refreshSendDefaults(userId: unknown): Promise<SendDefaults> {
    return (await refreshSendDefaultsWithSource(userId)).defaults;
}
